import { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import emailjs from '@emailjs/browser';
import Footer from '../home/Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import Icon from '../services/editable/commonIcon';
import EditableFieldSettings from '../services/editable/EditableFieldSettings';
import { buildMobileStyleSheet } from '../services/editable/styleUtils';
import { useEditablePage } from '../services/editable/useEditablePage';
import { useAuth } from '../../context/AuthContext';
import SignInModal from '../auth/SignInModal';
import contactUsLottie from '../../assets/gif/contact us.json';
import submittedLottie from '../../assets/gif/sucessfuly submitted.json';
import './ContactUs.css';
import '../services/editable/editable.css';

const EMAILJS_SERVICE_ID = 'service_hhqip4y';
const EMAILJS_TEMPLATE_ID = 'template_ge605zo';
const EMAILJS_PUBLIC_KEY = 'dlDyPXPu4yKCptTIL';

/**
 * Generic, CMS-driven Contact page — the Contact-page counterpart to
 * EditableAboutPage / TemplateServicePage. editable=false: plain public
 * render (byte-identical to the original ContactUs.jsx). editable=true: full
 * inline WYSIWYG (hero text/link, and the lead form's fields — type,
 * placeholder, required, dropdown options, add/remove — all editable via the
 * same EditableFieldSettings gear used on service pages).
 */
export default function EditableContactPage({ data, styles, editable = false, onChange, preview = false }) {
  const isEditing = editable && !preview;
  const navigate = useNavigate();
  const heroRef = useScrollAnimation({ threshold: 0.12, immediate: isEditing });
  const formRef = useScrollAnimation({ threshold: 0.08, immediate: isEditing });
  const successRef = useRef(null);

  const fields = data.form.fields || [];
  const initialForm = Object.fromEntries(fields.map((f) => [f.key, '']));
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ type: '', text: '' });
  const [sending, setSending] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const pendingSubmitRef = useRef(false);
  const { user, firebaseEnabled, isAuthSatisfied } = useAuth();

  useEffect(() => {
    if (status.type !== 'success' || !successRef.current) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    successRef.current.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
  }, [status.type]);

  const {
    T,
    RemoveBtn,
    AddBtn,
    SectionToggle,
    showSection,
    isSectionHidden,
    updateData,
    LinkPick,
  } = useEditablePage({ data, styles, onChange, isEditing });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (status.text || status.type === 'success') setStatus({ type: '', text: '' });
  };

  /** Builds templateParams and sends via EmailJS — the authenticated user's verified
   * email always wins over whatever (if anything) was typed into the email field. */
  const sendLead = async () => {
    const emailField = fields.find((f) => f.type === 'email');
    const emailTrim = user?.email || (emailField ? (form[emailField.key] || '').trim() : '');

    setSending(true);
    setStatus({ type: '', text: '' });

    const nameField = fields.find((f) => f.key === 'name') || fields.find((f) => f.type === 'text');
    const phoneField = fields.find((f) => f.type === 'tel');
    const subjectField = fields.find((f) => f.key === 'subject');
    const bodyField = fields.find((f) => f.type === 'textarea');
    const metaFields = fields.filter(
      (f) => f !== nameField && f !== emailField && f !== phoneField && f !== subjectField && f !== bodyField
    );
    const metaLines = metaFields.map((f) => `${f.label}: ${(form[f.key] || '').trim() || 'Not specified'}`);
    const bodyText = bodyField ? (form[bodyField.key] || '').trim() || ' - ' : '';

    const templateParams = {
      your_name: nameField ? (form[nameField.key] || '').trim() || ' - ' : ' - ',
      your_email: emailTrim,
      email: emailTrim,
      reply_to: emailTrim,
      phone_number: phoneField ? (form[phoneField.key] || '').trim() || ' - ' : ' - ',
      subject: subjectField ? (form[subjectField.key] || '').trim() || 'Contact form' : 'Contact form',
      message: [...metaLines, '', bodyText].join('\n'),
    };

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'form_submission',
        enhanced_conversion_data: { email: emailTrim, phone_number: phoneField ? form[phoneField.key] || undefined : undefined },
      });

      navigate('/thank-you');
    } catch (err) {
      setStatus({ type: 'error', text: err?.text || 'Something went wrong. Please try again or email us directly.' });
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) return; // admin editor: don't actually send email while editing
    const emailField = fields.find((f) => f.type === 'email');
    const emailTrim = emailField ? (form[emailField.key] || '').trim() : '';
    if (!user && emailField && !emailTrim) {
      setStatus({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    if (firebaseEnabled && !isAuthSatisfied) {
      pendingSubmitRef.current = true;
      setShowAuthModal(true);
      return; // form state is untouched — nothing typed is lost
    }

    await sendLead();
  };

  // Resumes a submission that was interrupted by the sign-in modal, once the visitor
  // is actually signed in (and verified, for password accounts).
  useEffect(() => {
    if (!pendingSubmitRef.current || !isAuthSatisfied) return;
    pendingSubmitRef.current = false;
    setShowAuthModal(false);
    sendLead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthSatisfied]);

  return (
    <div className="contact-us-page">
      <style>{buildMobileStyleSheet(styles, 640)}</style>

      {showSection('hero') ? (
        <div className="contact-us__top" data-svcpg-hidden={isEditing && isSectionHidden('hero') ? 'true' : undefined}>
          <div className="contact-us__frame">
            <section className="contact-us__hero" ref={heroRef} data-track-section="contact-hero">
              <SectionToggle sectionKey="hero" label="Hero" />
              <div className="contact-us__hero-inner">
                <div className="contact-us__hero-copy">
                  <span className="contact-us__pill">{T('hero.pill', 'hero.pill', data.hero.pill, 'span')}</span>
                  {T('hero.title', 'hero.title', data.hero.title, 'h1', 'contact-us__title')}
                  <p className="contact-us__lead">{T('hero.lead', 'hero.lead', data.hero.lead, 'span')}</p>
                  <p className="contact-us__meta">
                    {T('hero.metaPrefix', 'hero.metaPrefix', data.hero.metaPrefix, 'span')}{' '}
                    <Link to={data.hero.metaLinkTo} className="contact-us__inline-link" data-track-cta="book-consultation">
                      {T('hero.metaLinkLabel', 'hero.metaLinkLabel', data.hero.metaLinkLabel, 'span')}
                    </Link>
                    {LinkPick('hero.metaLinkTo', data.hero.metaLinkTo, <Icon name="arrowRight" size={13} />, 'Consultation link')}
                    .
                  </p>
                </div>
                <div className="contact-us__hero-visual" aria-hidden="true">
                  <Lottie className="contact-us__hero-lottie" animationData={contactUsLottie} loop autoplay />
                </div>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {showSection('form') ? (
        <section
          className="contact-us__form-section"
          ref={formRef}
          data-svcpg-hidden={isEditing && isSectionHidden('form') ? 'true' : undefined}
          data-track-section="contact-form-section"
        >
          <SectionToggle sectionKey="form" label="Lead form" />
          <div className="contact-us__form-shell">
            <form className="contact-us__form" onSubmit={handleSubmit} noValidate data-track-form="contact">
              <div className="contact-us__form-grid">
                {fields.map((field, i) => {
                  const wrapClass = `contact-us__field${field.fullWidth ? ' contact-us__field--full' : ''}${isEditing ? ' svcpg-form-field-wrap' : ''}`;
                  const isLockedEmail = field.type === 'email' && !!user;
                  const inputProps = {
                    name: field.key,
                    value: isLockedEmail ? user.email : form[field.key] || '',
                    onChange: handleChange,
                    required: !!field.required,
                    readOnly: isLockedEmail,
                  };
                  return (
                    <label className={wrapClass} key={field.key}>
                      <span className="contact-us__label">
                        {T(`form.fields.${i}.label`, `form.fields.${i}.label`, field.label, 'span')}
                        {field.required ? <span className="contact-us__req"> *</span> : null}
                        <EditableFieldSettings
                          editable={isEditing}
                          field={field}
                          onChange={(next) => updateData(`form.fields.${i}`, next)}
                        />
                      </span>
                      {field.type === 'textarea' ? (
                        <textarea rows={5} placeholder={field.placeholder} {...inputProps} />
                      ) : field.type === 'select' ? (
                        <select
                          {...inputProps}
                          className="contact-us__select"
                        >
                          <option value="">Select one...</option>
                          {(field.options || []).map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input type={field.type} placeholder={field.placeholder} {...inputProps} />
                      )}
                      <RemoveBtn path="form.fields" index={i} small label="Remove field" />
                    </label>
                  );
                })}
              </div>
              <AddBtn
                path="form.fields"
                label="Add field"
                template={(arr) => ({ key: `field_${Date.now()}_${arr.length}`, type: 'text', label: 'New field', placeholder: '', required: false })}
              />

              {status.text ? (
                <p className={`contact-us__feedback${status.type === 'error' ? ' contact-us__feedback--error' : ''}`} role="alert">
                  {status.text}
                </p>
              ) : null}

              <div className="contact-us__actions">
                <button type="submit" className="cta-button contact-us__submit" disabled={sending} aria-busy={sending} data-track-cta="submit-contact-form">
                  <span>{sending ? 'Sending…' : T('form.submitLabel', 'form.submitLabel', data.form.submitLabel, 'span')}</span>
                  <div className="cta-icon-circle">
                    <Icon name="arrowRight" size={16} />
                  </div>
                </button>
              </div>
            </form>

            {status.type === 'success' ? (
              <div ref={successRef} className="contact-us__success" role="status" aria-live="polite">
                <div className="contact-us__success-visual" aria-hidden="true">
                  <Lottie className="contact-us__success-lottie" animationData={submittedLottie} loop autoplay />
                </div>
                <div className="contact-us__success-copy">
                  {T('form.successTitle', 'form.successTitle', data.form.successTitle, 'h2', 'contact-us__success-title')}
                  <p className="contact-us__success-text">
                    {T('form.successText', 'form.successText', data.form.successText, 'span')}
                  </p>
                  <button type="button" className="contact-us__success-reset" onClick={() => setStatus({ type: '', text: '' })}>
                    {T('form.successResetLabel', 'form.successResetLabel', data.form.successResetLabel, 'span')}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <SignInModal open={showAuthModal} onClose={() => { setShowAuthModal(false); pendingSubmitRef.current = false; }} />

      <Footer />
    </div>
  );
}
